import * as THREE from 'three'

/** Dense organic tissue with a thin transmitting edge and a cinematic depth footprint. */
export function applyJellyMembrane(material: THREE.MeshPhysicalMaterial, minY = -0.65, maxY = 1): void {
  material.transparent = true
  material.opacity = 1
  // Milky tissue must participate in the cinematic depth buffer; otherwise
  // DOF blurs the distant seabed through its silhouette and across the eyes.
  material.depthWrite = true
  material.transmission = 0.86
  material.thickness = 0.52
  material.attenuationDistance = 0.95
  material.attenuationColor.copy(material.color)
  material.color.lerp(new THREE.Color(0xe0f3f5), 0.72)
  material.roughness = 0.34
  material.clearcoat = 0.34
  material.clearcoatRoughness = 0.28
  material.iridescence = 0.22
  material.side = THREE.FrontSide
  material.onBeforeCompile = shader => {
    shader.uniforms.membraneRange = { value: new THREE.Vector2(minY, maxY) }
    shader.vertexShader = 'varying float vMembraneHeight;\n' + shader.vertexShader
    shader.vertexShader = shader.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\nvMembraneHeight = position.y;')
    shader.fragmentShader = 'varying float vMembraneHeight;\nuniform vec2 membraneRange;\n' + shader.fragmentShader
    shader.fragmentShader = shader.fragmentShader.replace('#include <opaque_fragment>', `
      float membraneRim = pow(1.0 - abs(dot(normal, normalize(vViewPosition))), 2.2);
      float membraneThickness = smoothstep(membraneRange.x, membraneRange.y, vMembraneHeight);
      diffuseColor.a *= min(1.0, mix(0.96, 0.62, membraneRim) * mix(1.045, 1.0, membraneThickness));
      outgoingLight += mix(diffuse, vec3(0.7, 0.95, 1.0), 0.35) * membraneRim * 0.2;
      #include <opaque_fragment>
    `)
  }
  material.customProgramCacheKey = () => `jelly-membrane-v1-${minY}-${maxY}`
}

export function spongeMapChunk(): string {
  return THREE.ShaderChunk.map_fragment.replace('diffuseColor *= sampledDiffuseColor;', `
    sampledDiffuseColor.rgb = mix(vec3(0.075, 0.31, 0.58), sampledDiffuseColor.rgb, 0.8);
    diffuseColor *= sampledDiffuseColor;
  `)
}

/** Authored low-frequency roughness domains over fine grain, with no repeated
 * checker pattern. Shared within one character instance and disposed by its owner. */
export function makeCharacterSurfaceMap(kind: 'shell-roughness' | 'cloth-color' | 'cloth-normal'): THREE.DataTexture {
  const size = 128, height = new Float32Array(size * size), data = new Uint8Array(size * size * 4)
  let seed = 8753
  const random = () => { seed = Math.imul(seed, 1664525) + 1013904223 | 0; return (seed >>> 0) / 4294967296 }
  const grid = Float32Array.from({ length: 81 }, random)
  for (let y = 0; y < 9; y++) grid[y * 9 + 8] = grid[y * 9]
  for (let x = 0; x < 9; x++) grid[72 + x] = grid[x]
  const smooth = (n: number) => n * n * (3 - 2 * n)
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const u = x / size, v = y / size
    const gx = u * 8, gy = v * 8, ix = Math.floor(gx), iy = Math.floor(gy), fx = smooth(gx - ix), fy = smooth(gy - iy)
    const top = THREE.MathUtils.lerp(grid[iy * 9 + ix], grid[iy * 9 + ix + 1], fx)
    const bottom = THREE.MathUtils.lerp(grid[(iy + 1) * 9 + ix], grid[(iy + 1) * 9 + ix + 1], fx)
    const cloud = THREE.MathUtils.lerp(top, bottom, fy)
    const weave = Math.sin(u * Math.PI * 64) * Math.sin(v * Math.PI * 64)
    height[y * size + x] = kind === 'shell-roughness'
      ? 0.52 + cloud * 0.42 + (random() - 0.5) * 0.055
      : 0.84 + weave * 0.075 + cloud * 0.04
  }
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const i = (y * size + x) * 4
    if (kind === 'cloth-normal') {
      const dx = (height[y * size + (x + 1) % size] - height[y * size + (x + size - 1) % size]) * 2.2
      const dy = (height[((y + 1) % size) * size + x] - height[((y + size - 1) % size) * size + x]) * 2.2
      const normal = new THREE.Vector3(-dx, -dy, 1).normalize()
      data[i] = Math.round((normal.x * 0.5 + 0.5) * 255)
      data[i + 1] = Math.round((normal.y * 0.5 + 0.5) * 255)
      data[i + 2] = Math.round((normal.z * 0.5 + 0.5) * 255)
    } else {
      data[i] = data[i + 1] = data[i + 2] = Math.round(height[y * size + x] * 255)
    }
    data[i + 3] = 255
  }
  const texture = new THREE.DataTexture(data, size, size)
  texture.name = `Character_${kind}`
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping
  if (kind.startsWith('cloth')) texture.repeat.set(3, 3)
  texture.magFilter = THREE.LinearFilter
  texture.minFilter = THREE.LinearMipmapLinearFilter
  texture.generateMipmaps = true
  texture.needsUpdate = true
  return texture
}
